import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import ActionIconButton from '../../../../components/ui/ActionIconButton';
import NoData from '../../../../components/common/NoData';

import DefaultImage from '../../../../assets/images/profile/default-profile.png';
import { addCourseReview, getCourseReviews, deleteReview, editCourseReview } from '../../../../services/user/course';
import { toast } from '../../../../components/ui/notify';
import { useTimezone } from '../../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../../utils/timezone';

/** S3 keys / relative paths need a base URL; presigned https URLs pass through (backend should return signed URLs). */
function resolveAvatarSrc(path) {
  if (path == null || typeof path !== 'string') return null;
  const t = path.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  const base = (process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || '')
    .replace(/['"]/g, '')
    .replace(/\/$/, '');
  if (!base) return t;
  return `${base}/${t.replace(/^\//, '')}`;
}

function CourseRating({ contentData }) {
  const { timeZone } = useTimezone();
  const formatRatingDate = (value) => formatUtcInTimeZone(value, timeZone, 'MMM DD, YYYY');
  const [ratings, setRatings] = useState([]);
  const [showInput, setShowInput] = useState(false);
  const [ratingText, setRatingText] = useState('');
  const [selectedRating, setSelectedRating] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUserRated, setHasUserRated] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editReviewId, setEditReviewId] = useState(null);

  const videoId = contentData?.id;
  const courseId = contentData?.course_id;

  const user = JSON.parse(localStorage.getItem('user'));
  const userId = user?.id;

  useEffect(() => {
    if (videoId && courseId) {
      fetchReviews();
    }
  }, [videoId, courseId]);

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      const response = await getCourseReviews(courseId, videoId);
      if (response.success) {
        setRatings(response.data);
        // Check if current user has already rated
        const userHasRated = response.data.some(rating => String(rating.user_id) === String(userId));
        setHasUserRated(userHasRated);
      }
    } catch (error) {
      toast.error('Failed to load reviews');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRating = () => {
    setShowInput(true);
    setIsEditing(false);
    setEditReviewId(null);
    setRatingText('');
    setSelectedRating(0);
  };

  const handleStarClick = (rating) => {
    setSelectedRating(rating);
  };

  const handleSaveRating = async () => {
    if (selectedRating > 0 && !isSubmitting) {
      try {
        setIsSubmitting(true);
        let response;

        if (isEditing && editReviewId) {
          response = await editCourseReview({
            id: editReviewId,
            class_rating: selectedRating,
            review_text: typeof ratingText === 'string' ? ratingText.trim() : ratingText,
          });

          if (response.success) {
            toast.success('Review updated successfully');
          }
        } else {
          response = await addCourseReview({
            course_id: courseId,
            video_id: videoId,
            class_rating: selectedRating,
            mentor_rating: 0,
            review_text: typeof ratingText === 'string' ? ratingText.trim() : ratingText,
          });

          if (response.success) {
            toast.success('Review added successfully');
          }
        }

        if (response.success) {
          await fetchReviews();
          setRatingText('');
          setSelectedRating(0);
          setShowInput(false);
          setIsEditing(false);
          setEditReviewId(null);
        } else {
          toast.error('Failed to save review');
        }
      } catch (error) {
        toast.error('Failed to save review');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDeleteReview = async (review_id) => {
    try {
      const response = await deleteReview(review_id);
      if (response.success) {
        await fetchReviews();
        toast.success('Review deleted successfully');
      } else toast.error('Failed to delete review');
    } catch (error) {
      // Error deleting review
      toast.error('Failed to delete review');
    }
  };

  const handleEditReview = (rating) => {
    setIsEditing(true);
    setEditReviewId(rating.review_id);
    setRatingText(rating.review_text || '');
    setSelectedRating(rating.class_rating || 0);
    setShowInput(true);
  };

  if (!videoId || !courseId) {
    return <NoData message="No lesson selected" description="Choose a lesson to view course ratings." compact />;
  }

  const renderStars = (rating, interactive = false) => Array.from({ length: 5 }, (_, index) => {
    const star = <Icon icon="mdi:star" width="20" height="20" aria-hidden="true" />;
    const color = index < Number(rating) ? 'tw-text-amber-400' : 'tw-text-slate-300';
    return interactive ? (
      <button
        key={index}
        type="button"
        className={`tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 hover:tw-bg-amber-50 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-indigo-500 ${color}`}
        onClick={() => handleStarClick(index + 1)}
        aria-label={`${index + 1} star${index === 0 ? '' : 's'}`}
        aria-pressed={Number(rating) === index + 1}
      >{star}</button>
    ) : <span key={index} className={`tw-inline-flex tw-h-5 tw-w-5 ${color}`}>{star}</span>;
  });

  if (isLoading) {
    return (
      <div className="tw-text-center tw-p-6">
        <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600" role="status">
          <span className="tw-sr-only">Loading reviews...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-[767px]:tw-min-h-[auto] tw-bg-[#fff] tw-min-h-[calc(100vh_-_400px)] tw-flex tw-flex-col course-rating-container tw-p-0">
      <div className="tw-mb-[20px] tw-flex tw-justify-end rating-header">
        <button
          className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}
          onClick={handleAddRating}
          disabled={hasUserRated || isSubmitting}
          title={hasUserRated ? "You have already rated this course" : "Add your rating"}
        >
          <Icon icon="mdi:plus" width="16" height="16" />
          {hasUserRated ? "Already Rated" : "Add Rating"}
        </button>
      </div>

      {showInput && (
        <div className="tw-bg-[#f8f9fa] tw-border-[1px] tw-border-solid tw-border-[#e0e0e0] tw-rounded-[8px] tw-p-[20px] tw-mb-[20px] tw-shadow-[0_2px_10px_rgba(0,_0,_0,_0.05)] rating-input-container">
          <div className="tw-flex tw-gap-1 tw-mb-4 tw-justify-center" role="group" aria-label="Your star rating">
            {renderStars(selectedRating, !isSubmitting)}
          </div>
          <textarea
            className={"tw-w-full tw-min-h-[100px] tw-p-[15px] tw-border-[1px] tw-border-solid tw-border-[#e0e0e0] tw-rounded-[8px] [resize:none] [font-family:inherit] tw-text-[15px] tw-mb-[20px] tw-bg-[white] tw-text-[#555] [&:focus]:[outline:none] [&:focus]:tw-border-[#3152e8] rating-input"}
            placeholder="Write your review..."
            value={ratingText}
            onChange={(e) => setRatingText(e.target.value)}
            disabled={isSubmitting}
            onFocus={(e) => {
              requestAnimationFrame(() => {
                e.target.scrollIntoView({ block: 'center', behavior: 'smooth' });
              });
            }}
          ></textarea>
          <div className="tw-flex tw-justify-end tw-gap-[15px] rating-actions">
            <button
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-secondary"}
              onClick={() => {
                setRatingText('');
                setSelectedRating(0);
                setShowInput(false);
                setIsEditing(false);
                setEditReviewId(null);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-inline-flex tw-items-center tw-justify-center tw-gap-2"}
              onClick={handleSaveRating}
              disabled={selectedRating === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm" role="status" aria-hidden="true" />
                  {isEditing ? 'Updating...' : 'Submitting...'}
                </>
              ) : (
                isEditing ? 'Update' : 'Submit'
              )}
            </button>
          </div>
        </div>
      )}

      <div className={"max-[767px]:tw-max-h-[none] max-[767px]:tw-overflow-y-visible max-[767px]:tw-pr-0 [flex:1] tw-overflow-y-auto tw-max-h-[calc(100vh_-_550px)] tw-pr-[10px] [&::-webkit-scrollbar]:tw-w-[6px] [&::-webkit-scrollbar-track]:tw-bg-[#f1f1f1] [&::-webkit-scrollbar-track]:tw-rounded-[3px] [&::-webkit-scrollbar-thumb]:tw-bg-[#c1c1c1] [&::-webkit-scrollbar-thumb]:tw-rounded-[3px] [&::-webkit-scrollbar-thumb:hover]:tw-bg-[#a8a8a8] ratings-list"}>
        {ratings.length > 0 ? (
          ratings.map(rating => (
            <div key={rating.review_id} className={"tw-bg-[#fff] tw-border-[1px] tw-border-solid tw-border-[#e0e0e0] tw-rounded-[8px] tw-p-[16px] tw-mb-[16px] [transition:transform_0.2s_ease,_box-shadow_0.2s_ease] [&:hover]:[transform:translateY(-2px)] [&:hover]:tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.05)] rating-item"}>
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-[12px] rating-item-header">
                <div className="tw-flex tw-items-center tw-gap-[12px] rating-author">
                  <img
                    src={resolveAvatarSrc(rating.user?.profile_picture) || DefaultImage}
                    alt={rating.user?.name ? `${rating.user.name} avatar` : 'User avatar'}
                    className="tw-w-[40px] tw-h-[40px] tw-rounded-[50%] tw-object-cover tw-border-[2px] tw-border-solid tw-border-[#f0f0f0] author-avatar"
                  />
                  <span className="tw-font-semibold tw-text-[#333] tw-text-[15px] author-name">{rating.user?.name || 'Anonymous'}</span>
                </div>
                <div className="tw-flex tw-justify-end tw-gap-[15px] rating-actions">
                  <span className="tw-text-[#666] tw-text-[13px] rating-date">
                    {formatRatingDate(rating.review_createdAt)}
                  </span>
                  {String(userId) === String(rating.user_id) && (
                    <>
                      <ActionIconButton icon="tabler:pencil" label="Edit review" tone="edit" onClick={() => handleEditReview(rating)} />
                      <ActionIconButton icon="tabler:trash" label="Delete review" tone="danger" onClick={() => handleDeleteReview(rating.review_id)} />
                    </>
                  )}
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1 tw-my-2" aria-label={`${rating.class_rating} out of 5 stars`}>
                {renderStars(rating.class_rating)}
              </div>
              {rating.review_text && <p className="tw-m-[12px_0_0_0] tw-text-[#444] tw-text-[14px] tw-whitespace-pre-wrap rating-text">{rating.review_text}</p>}
            </div>
          ))
        ) : (
          <NoData
            message="No Ratings Yet"
            description="Be the first to rate this content!"
            icon="mdi:star-outline"
          />
        )}
      </div>
    </div>
  );
}

export default CourseRating;
